import React from 'react';
import { SelectOptionType } from '@aidkitorg/component-library';
import { SupportRequest } from '../../SupportUtil';
import { SupportedLanguage } from '../../Context';
import { snakeToEnglish } from '../../Util';
import { SupportExtraColumnConfig } from './types';

export type ChannelConfig = { limitedToTags?: string[]; supportDashboard?: { limitedToTags?: string[] } };

/**
 * Check if a user has access to a support channel by verifying both
 * channel-level and supportDashboard-level limitedToTags.
 */
export const canAccessSupportChannel = (channel: ChannelConfig | undefined, userTags?: string[]): boolean => {
  if (!channel) return false;
  if (channel.limitedToTags?.length && (!userTags || !channel.limitedToTags.some(tag => userTags.includes(tag)))) return false;
  const sdTags = channel.supportDashboard?.limitedToTags;
  if (sdTags?.length && (!userTags || !sdTags.some(tag => userTags.includes(tag)))) return false;
  return true;
};

export type StatusColors = {
  dotClass: string;
  textClass: string;
  borderClass: string;
};

const STATUS_COLORS: Record<string, StatusColors> = {
  open:      { dotClass: 'bg-blue-500 ring-2 ring-blue-500/25',    textClass: 'text-blue-600 font-semibold',   borderClass: '!border-l-[3px] !border-l-blue-500' },
  escalated: { dotClass: 'bg-red-500 ring-2 ring-red-500/25',      textClass: 'text-red-600 font-semibold',    borderClass: '!border-l-[3px] !border-l-red-500' },
  resolved:  { dotClass: 'bg-green-500 ring-2 ring-green-500/25',  textClass: 'text-green-600 font-semibold',  borderClass: '!border-l-[3px] !border-l-green-500' },
  viewed:    { dotClass: 'bg-yellow-500 ring-2 ring-yellow-500/25', textClass: 'text-yellow-600 font-semibold', borderClass: '!border-l-[3px] !border-l-yellow-500' },
};

const DEFAULT_STATUS_COLORS: StatusColors = {
  dotClass: 'bg-gray-400',
  textClass: 'text-gray-500',
  borderClass: 'pl-[2px]',
};

export const getStatusColors = (status: string | undefined | null): StatusColors =>
  (status && STATUS_COLORS[status]) ? STATUS_COLORS[status] : DEFAULT_STATUS_COLORS;

/**
 * Status options for support cases with colored dots.
 * Used by SupportCaseDetailPage's status selector.
 */
export const STATUS_OPTIONS: SelectOptionType[] = [
  {
    label: (
      <div className="flex items-center gap-2">
        <div className={`w-2 h-2 rounded-full ${STATUS_COLORS.open.dotClass}`} />
        <span>Open</span>
      </div>
    ),
    value: 'open',
  },
  {
    label: (
      <div className="flex items-center gap-2">
        <div className={`w-2 h-2 rounded-full ${STATUS_COLORS.escalated.dotClass}`} />
        <span>Escalated</span>
      </div>
    ),
    value: 'escalated',
  },
  {
    label: (
      <div className="flex items-center gap-2">
        <div className={`w-2 h-2 rounded-full ${STATUS_COLORS.viewed.dotClass}`} />
        <span>Viewed</span>
      </div>
    ),
    value: 'viewed',
  },
  {
    label: (
      <div className="flex items-center gap-2">
        <div className={`w-2 h-2 rounded-full ${STATUS_COLORS.resolved.dotClass}`} />
        <span>Resolved</span>
      </div>
    ),
    value: 'resolved',
  },
];

/**
 * Detect if a contact string is a phone number
 */
export const isPhoneContact = (contact: string): boolean => {
  return contact.startsWith('+') || /^\d+$/.test(contact);
};

/**
 * Detect if a contact string is an email
 */
export const isEmailContact = (contact: string): boolean => {
  return contact.includes('@');
};

/**
 * Get the contact type label for display
 */
export const getContactTypeLabel = (contact: string): string => {
  if (isEmailContact(contact)) {
    return 'Email Address';
  }
  if (isPhoneContact(contact)) {
    return 'Phone Number';
  }
  return 'Contact';
};

/**
 * Get primary contact method icon name for a support case
 */
export const getContactMethodIcon = (
  contactMethods: string[] | undefined | null
): 'EnvelopeIcon' | 'DevicePhoneMobileIcon' | 'PhoneIcon' => {
  if (!contactMethods || contactMethods.length === 0) return 'EnvelopeIcon';

  if (contactMethods.includes('email')) return 'EnvelopeIcon';
  if (contactMethods.includes('sms') || contactMethods.includes('mms')) return 'DevicePhoneMobileIcon';
  if (contactMethods.includes('call')) return 'PhoneIcon';

  return 'EnvelopeIcon';
};

/**
 * Get the display name or contact info for a support case
 * Returns the applicant's name if available, otherwise falls back to the source (phone/email)
 * @param request - The support request object
 * @param fallbackText - Optional fallback text when no name or source is available (defaults to 'Unknown')
 */
export const getDisplayName = (request: SupportRequest, fallbackText: string = 'Unknown'): string => {
  const { row, applicantInfos } = request;

  // Try to get name from applicantInfos
  const allApplicantInfos = Object.values(applicantInfos);
  if (allApplicantInfos.length === 1) {
    const firstInfo = allApplicantInfos[0];
    const name = firstInfo?.legal_name || firstInfo?.name || firstInfo?.first_name;
    if (name) return name;
  }

  // Fall back to source (phone/email)
  return row.source || fallbackText;
};

export type ExtraColumnBadge = { label?: string; value: string };

/**
 * Build the extra-column badges for a support case from the dashboard's
 * configured target fields (`targetFieldConfigs`). Mirrors the value derivation
 * used by the classic support table (SupportTableV3.mergeBaseAndExtraColumns):
 * each field's value is collected across every matched applicant, empties are
 * dropped, and the rest joined. Configs are ordered by `columnIndex` for a
 * stable badge order; a `label` of `'hide'` yields a value-only badge.
 */
export const getExtraColumnBadges = (
  request: SupportRequest,
  extraColumns: SupportExtraColumnConfig[] | undefined,
  lang: SupportedLanguage = 'en',
): ExtraColumnBadge[] => {
  if (!extraColumns?.length) return [];
  const applicantInfoValues = Object.values(request.applicantInfos ?? {});

  return [...extraColumns]
    .sort((a, b) => (a.columnIndex ?? 999) - (b.columnIndex ?? 999))
    .map((col): ExtraColumnBadge | null => {
      const value = applicantInfoValues
        .map((info) => info?.[col.targetField])
        .filter((v) => v != null && v !== '')
        .join(', ');
      if (!value) return null;
      const label = col.consolidatedColumn?.label === 'hide'
        ? undefined
        : col.consolidatedColumn?.label?.[lang]
          || col.consolidatedColumn?.label?.en
          || snakeToEnglish(col.targetField);
      return { label, value };
    })
    .filter((b): b is ExtraColumnBadge => b !== null);
};
